import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { investorProjects, marketStats } from '@/mocks/investorsData';

const FORM_URL = 'https://readdy.ai/api/form/d8j8b6kcl43d0bibf70g';

export default function Investors() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', organization: '', email: '', phone: '', interest: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    try {
      const fd = new URLSearchParams();
      fd.append('name', form.name);
      fd.append('organization', form.organization);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('interest', form.interest);
      fd.append('message', form.message);
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: fd.toString(),
      });
      setStatus('success');
      setForm({ name: '', organization: '', email: '', phone: '', interest: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-primary-900">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight max-w-4xl mx-auto">
            {t('investors.title')}
          </h1>
          <p className="text-sm md:text-base text-background-50/70 mt-4 max-w-2xl mx-auto">
            {t('investors.subtitle')}
          </p>
        </div>
      </section>

      {/* Vision */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('investors.vision')}</h2>
            <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-3xl mx-auto leading-relaxed">{t('investors.visionDesc')}</p>
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="space-y-8">
            {investorProjects.map((project, idx) => (
              <div key={project.id} className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-6 lg:gap-10 items-center`}>
                <div className="w-full lg:w-1/2">
                  <div className="rounded-2xl overflow-hidden">
                    <img src={project.image} alt={t(project.titleKey)} className="w-full h-[250px] md:h-[320px] object-cover object-top" />
                  </div>
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 mb-4">
                    <i className={`${project.icon} text-xl text-accent-600`}></i>
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">{t(project.titleKey)}</h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{t(project.descKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Market Stats */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{t('investors.market')}</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {marketStats.map((stat, idx) => (
              <div key={idx} className="bg-background-100 rounded-2xl p-6 text-center border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-4">
                  <i className={`${stat.icon} text-lg text-accent-600`}></i>
                </div>
                <div className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-1">{t(stat.titleKey)}</div>
                <p className="text-xs md:text-sm text-foreground-500">{t(stat.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA + Form */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          {!showForm ? (
            <div>
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 px-8 py-4 bg-accent-500 text-background-50 font-semibold text-sm rounded-full hover:bg-accent-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {t('investors.cta')}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-xs"></i>
                </div>
              </button>
              <p className="text-xs text-foreground-500 mt-4">{t('investors.contact')}</p>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70 space-y-4 text-left">
              <h3 className="font-heading text-xl font-semibold text-foreground-950 text-center">{t('investors.cta')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('investors.form.name')}</label>
                  <input type="text" name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('investors.form.organization')}</label>
                  <input type="text" name="organization" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors" required />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('investors.form.email')}</label>
                  <input type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors" required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('investors.form.phone')}</label>
                  <input type="tel" name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('investors.form.interest')}</label>
                <select name="interest" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors" required>
                  <option value="">--</option>
                  {investorProjects.map((p) => (<option key={p.id} value={t(p.titleKey)}>{t(p.titleKey)}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('investors.form.message')}</label>
                <textarea name="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} maxLength={500} className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors resize-none" required></textarea>
              </div>
              <div className="flex items-center gap-3">
                <button type="submit" disabled={status === 'loading'} className="px-8 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60">
                  {status === 'loading' ? '...' : t('investors.form.cta')}
                </button>
                {status === 'success' && <span className="text-sm text-green-600 font-medium">Message envoyé !</span>}
                {status === 'error' && <span className="text-sm text-red-500 font-medium">Erreur, réessayez.</span>}
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
      <a href="https://wa.me/237687696969" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:bg-[#22c55e] transition-all duration-300 hover:scale-110 cursor-pointer" aria-label="Contact WhatsApp">
        <i className="ri-whatsapp-line text-2xl md:text-3xl"></i>
      </a>
    </main>
  );
}